import type { Box3 } from 'three';

import { BOUNDBOX_VIEWPORT_FACTOR } from './constants';
import { getBoundingBoxDiagonal, isClose } from './geom-utils';

// factor of top-to-bottom canvas viewport that will dictate the fidelity of intersection checks
export const INTERSECTION_SCALE_FACTOR = 0.01;

type ViewPortJson = {
  left: number;
  right: number;
  top: number;
  bottom: number;
};

export class ViewPort implements ViewPortJson {
  public left: number;
  public right: number;
  public top: number;
  public bottom: number;

  constructor(obj?: Partial<ViewPortJson> | undefined) {
    const { left, right, top, bottom } = obj || {};
    this.left = left || 0;
    this.right = right || 0;
    this.top = top || 0;
    this.bottom = bottom || 0;
  }

  get diagonal(): number {
    const width = this.right - this.left;
    const height = this.top - this.bottom;
    return Math.sqrt(width * width + height * height);
  }

  get offset(): { x: number; y: number } {
    return {
      x: (this.left + this.right) / 2,
      y: (this.top + this.bottom) / 2,
    };
  }

  get aspectRatio(): number {
    return Math.abs(this.right - this.left) / Math.abs(this.top - this.bottom);
  }

  get ratio(): number {
    return Math.abs(this.top - this.bottom) / Math.abs(this.right - this.left);
  }

  isEmpty(): boolean {
    return !(
      Number.isFinite(this.left) &&
      Number.isFinite(this.right) &&
      Number.isFinite(this.top) &&
      Number.isFinite(this.bottom)
    );
  }

  toObject(): ViewPortJson {
    return {
      left: this.left,
      right: this.right,
      top: this.top,
      bottom: this.bottom,
    };
  }

  //Note: obj is typically OrthographicCamera
  static fromObject(obj: ViewPortJson): ViewPort {
    return new ViewPort({
      left: obj.left,
      right: obj.right,
      top: obj.top,
      bottom: obj.bottom,
    });
  }

  clone(): ViewPort {
    return new ViewPort({
      left: this.left,
      right: this.right,
      top: this.top,
      bottom: this.bottom,
    });
  }

  applyScale(scale: number): void {
    const width = this.right - this.left;
    const height = this.top - this.bottom;
    const midWidth = (this.right + this.left) / 2;
    const midHeight = (this.top + this.bottom) / 2;
    const halfScale = scale / 2;
    const halfWidthTimesScale = width * halfScale;
    const halfHeightTimesScale = height * halfScale;
    this.right = midWidth + halfWidthTimesScale;
    this.left = midWidth - halfWidthTimesScale;
    this.top = midHeight + halfHeightTimesScale;
    this.bottom = midHeight - halfHeightTimesScale;
  }

  applyOffset(offset: { x: number; y: number }): void {
    this.left += offset.x;
    this.right += offset.x;
    this.top += offset.y;
    this.bottom += offset.y;
  }

  applyZoom(zoom: number): void {
    if (zoom === 1) {
      return;
    }
    this.left /= zoom;
    this.right /= zoom;
    this.top /= zoom;
    this.bottom /= zoom;
  }

  static getForBoundingBoxDiagonal(
    diagonal: number,
    nodeHeight: number,
    nodeWidth: number,
    factor = BOUNDBOX_VIEWPORT_FACTOR,
  ): ViewPort {
    const heightWidthRatio = nodeHeight / nodeWidth;
    const angle = Math.atan(heightWidthRatio);
    const halfWidth = Math.abs(diagonal * Math.cos(angle) * factor) / 2;
    const halfHeight = halfWidth * heightWidthRatio;
    return new ViewPort({
      left: -halfWidth,
      right: halfWidth,
      top: halfHeight,
      bottom: -halfHeight,
    });
  }

  static getForBoundingBox(
    boundingBox: Box3,
    nodeHeight: number,
    nodeWidth: number,
    factor = BOUNDBOX_VIEWPORT_FACTOR,
  ): ViewPort {
    const diagonal = getBoundingBoxDiagonal(boundingBox);
    return ViewPort.getForBoundingBoxDiagonal(
      diagonal,
      nodeHeight,
      nodeWidth,
      factor,
    );
  }

  updateObjectProps(obj: ViewPortJson): void {
    obj.left = this.left;
    obj.right = this.right;
    obj.top = this.top;
    obj.bottom = this.bottom;
  }

  /*
    Collects the radius for a SphereGeometry object as a fraction of vertical FOV
  */
  getVertexRadius(factor: number): number {
    return factor * (this.top - this.bottom);
  }

  static isCloseViewport(
    v1: ViewPort | undefined | null,
    v2: ViewPort | undefined | null,
  ): boolean {
    return (
      v1 != null &&
      v2 != null &&
      isClose(v1.left, v2.left) &&
      isClose(v1.right, v2.right) &&
      isClose(v1.top, v2.top) &&
      isClose(v1.bottom, v2.bottom)
    );
  }
}
export default ViewPort;
